<template>
    <div>
        <div v-if="!pending && licenses.length > 0">
            <div class="row justify-between">
                <div>
                    <div class="text-h6 row items-center q-mb-sm">
                        <q-icon name="mdi-select-multiple" size="sm" class="q-mr-xs" />{{ typeText }} {{ upgradeRenewText | toProperCase }} Selection
                    </div>
                    <p>Please select from the list which {{ productCategoryText }} {{ typeText | toLowerCase }}(s) to use for {{ upgradeRenewText }}<span v-if="targetEditionText"> to <strong>{{ targetEditionText }}</strong> edition</span>.</p>
                </div>
                <div v-if="(!this.product || (this.product && !this.product.isUpgrade)) && this.licenses.length > 1">
                    <expiry-sync-date-setting :isFromMultiLicenseSelection="true" @expirySyncDateSettingUpdated="expirySyncDateSettingUpdated" />
                </div>
            </div>
            <div class="row text-negative text-caption items-center" v-if="showSelectionRequired">
                <q-icon name="mdi-alert-circle" class="text-negative q-pr-xs" style="font-size: 1.25em" /> A {{ typeText }} selection is required
            </div>
            <div class="row wrap">
                <div v-for="license in form.licenses" :key="license.id"
                     class="col-xs-12 col-md-6 col-lg-4" style="border: 1px solid lightgrey; margin-right: -1px; margin-bottom: -1px;">
                    <q-item tag="label" v-ripple @click="showSelectionRequired = false" class="full-height" :disabled="isLicenseInCart(license) || isLicenseOutsideOfSyncedRenewalScope(license)">
                        <div class="column full-width justify-between" style="height: 100%;">
                            <div class="row no-wrap items-start">
                                <q-item-section side top>
                                    <q-checkbox :id="license.id" v-model="license.checked" v-if="!isLicenseInCart(license)" :disable="isLicenseInCart(license) || isLicenseOutsideOfSyncedRenewalScope(license)" />
                                    <q-icon v-if="isLicenseInCart(license)" name="mdi-checkbox-marked" color="primary" size="sm" />
                                </q-item-section>
                                <q-item-section top>
                                    <q-item-label overline>{{ license.licenseKeyID }}</q-item-label>

                                    <q-item-label caption>{{ license.productDescription }}</q-item-label>

                                    <q-item-label caption v-if="license.oemName">OEM: {{ license.oemName }}</q-item-label>

                                    <q-item-label caption v-if="getLicenseStatus(license.expiry)">Expires On: {{ license.expiry | date }}</q-item-label>
                                    <q-item-label caption v-else>Expired On: {{ license.expiry | date }}</q-item-label>
                                </q-item-section>
                                <q-item-section side top style="justify-content: space-between; height: 100%;">
                                    <q-badge transparent v-if="getLicenseStatus(license.expiry)" color="positive">Active</q-badge>
                                    <q-badge transparent v-else color="negative">Expired</q-badge>
                                    <div v-if="isLicenseInCart(license)" style="width: min-content; display: flex;">
                                        <q-icon name="mdi-cart" color="primary" size="sm" />
                                        <div style="font-size: 0.6rem;" class="q-pl-xs">Already in Cart</div>
                                    </div>
                                </q-item-section>
                            </div>
                            <div v-if="isLicenseOutsideOfSyncedRenewalScope(license)"
                                 class="q-mt-sm full-width text-negative text-caption q-border-top"
                                 style="font-size: 0.7rem;">
                                {{ license.expirySyncNotApplicableMessage }}
                            </div>
                        </div>
                    </q-item>
                </div>
            </div>
        </div>
        <div v-if="pending" class="q-pa-xl">
            <q-inner-loading :showing="pending">
                <div>
                    <q-spinner color="primary"
                               :thickness="2"
                               size="3em" />
                </div>
            </q-inner-loading>
        </div>
        <div v-if="!pending && licenses.length <= 0">
            <div class="text-h6 row items-center q-mb-sm">
                <q-icon name="mdi-select-off" size="sm" class="q-mr-xs" />No {{ typeText }} Found
            </div>
            <p>
                A valid {{ productCategoryText }} {{ typeText | toLowerCase }} could not be found for {{ upgradeRenewText }}<span v-if="targetEditionText"> to <strong>{{ targetEditionText }}</strong> edition</span>. You can purchase a new {{ typeText | toLowerCase }} on the previous step or at our <router-link :to="{ name:'/store', params: { productLine: productCategoryText.toLowerCase() }}">Store</router-link>.
                For further assistance please contact <router-link to="/support/ticket">support</router-link> or call 403.247.8566.
            </p>
        </div>
    </div>
</template>
<script>

    import { cloneDeep } from 'lodash'
    import expirySyncDateSetting from '@/components/ExpirySyncDateSetting'

    export default {
        name: 'multi-license-selection',
        components: {
            expirySyncDateSetting,
        },
        props: {
            licenses: Array,
            product: Object,
            pending: Boolean,
        },

        data() {
            return {
                form: {
                    licenses: null,
                },
                showSelectionRequired: false,
            }
        },

        methods: {
            syncLicenses() {
                if (this.licenses) {
                    let data = cloneDeep(this.licenses)
                    data.forEach((x) => {
                        x.checked = false;
                    });
                    this.form.licenses = data
                }
            },
            onSubmit() {
                //onSubmit is being called from the parent component
                let selectedLicenses = this.form.licenses?.filter(obj => { return obj.checked == true })
                if (!selectedLicenses || selectedLicenses.length < 1) {
                    this.showSelectionRequired = true
                    return
                }
                this.$emit('submitted', selectedLicenses)
            },
            expirySyncDateSettingUpdated() {
                this.syncLicenses()
                this.$emit('expirySyncDateSettingUpdated')
            },
            isLicenseInCart(license) {
                return this.cart.cartItems && this.cart.cartItems.some(i => i.licenseID === license.id)
            },
            isLicenseOutsideOfSyncedRenewalScope(license) {
                // If product passed in is an upgrade, and the license is not expired then the expiry date does not need to be taken into consideration
                if (this.product && this.product.isUpgrade && this.getLicenseStatus(license.expiry)) return false;
                // If expiry sync is not enabled, the license selection cannot be disabled based on expiry sync settings, return false
                if (!license.expirySyncEnabled) return false;
                // If expiry sync is enabled and applicable for the license, the license selection is not outside of the renewal scope, return false
                if (license.expirySyncApplicable) return false;
                // Otherwise, expiry sync is enabled and not applicable for the license, return true
                return true;
            },
        },
        computed: {
            cart() { return this.$store.getters.cart },
            targetEditionText() {
                if (this.product && this.product.isUpgrade)
                    return (this.product.id.includes('MJ8') ? 'MJ8' : 'Professional')
                return ''
            },
            upgradeRenewText() {
                if (this.product) {
                    return (this.product.isUpgrade ? 'upgrade' : 'renewal')
                }
                return 'renewal'
            },
            typeText() {
                if (this.product && this.product.isAnnualSubscription)
                    return 'Subscription'
                return 'License'
            },
            productCategoryText() {
                if (this.product) {
                    return this.product.productCategoryID
                }
                return ''
            },
        },
        created() {
            this.syncLicenses()
        },
        watch: {
            licenses(newVal, oldVal) {
                if (newVal && newVal !== oldVal)
                    this.syncLicenses()
            },
        }

    }
</script>
